import {
  Component,
  EventEmitter,
  Output,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CountryCode, parsePhoneNumber, AsYouType } from 'libphonenumber-js';
import { NgxUiLoaderModule, NgxUiLoaderService } from 'ngx-ui-loader';
import { AngularPhoneNumberInput } from 'angular-phone-number-input';
import { AuthService } from '../../../../core/services/auth.service';
import { ModalService } from '../../../../core/services/modal.service';
import { VerifyOtpComponent } from '../verify-otp/verify-otp.component';
import { RequestOtpDto } from '../../../../core/interfaces/auth.interface';
import { SwalService } from '../../../../core/services/swal.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-verify-phone-number',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgxUiLoaderModule,
    AngularPhoneNumberInput,
  ],
  templateUrl: './verify-phone-number.component.html',
  styleUrl: './verify-phone-number.component.scss',
})
export class VerifyPhoneNumberComponent implements OnInit, OnDestroy {
  @Output() otpRequested = new EventEmitter<{
    otpId: string;
    phoneNumber: string;
    indicative: string;
  }>();
  preferredCountries: string[] = ['SN', 'CI', 'CM'];

  verifyPhoneNumberForm: FormGroup;
  loading = false;
  submitted = false;
  private subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private modalService: ModalService,
    private ngxUiLoaderService: NgxUiLoaderService,
    private swalService: SwalService
  ) {
    this.verifyPhoneNumberForm = this.fb.group({
      phoneNumber: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    // Écouter les événements du service modal
    this.subscription.add(
      this.modalService.getEvents().subscribe((event) => {
        if (event.type === 'otp_verified') {
          this.onOtpVerified(event.data);
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onSubmit() {
    this.submitted = true;

    if (this.verifyPhoneNumberForm.invalid) {
      return;
    }

    const phoneControl = this.verifyPhoneNumberForm.get('phoneNumber');
    if (!phoneControl?.value) {
      return;
    }

    this.loading = true;
    this.ngxUiLoaderService.startLoader('verify-phone-loader');

    console.log({ phoneControl });

    // angular-phone-number-input peut retourner une chaîne ou un objet
    const phoneValue = phoneControl.value;
    let fullPhoneNumber = '';

    // Si c'est un objet, extraire le numéro international
    if (typeof phoneValue === 'object' && phoneValue !== null) {
      if (phoneValue.internationalNumber) {
        fullPhoneNumber = phoneValue.internationalNumber;
      } else if (phoneValue.number && phoneValue.countryCode) {
        fullPhoneNumber = `+${phoneValue.countryCode}${phoneValue.number}`;
      } else {
        fullPhoneNumber = phoneValue.toString();
      }
    } else {
      fullPhoneNumber = phoneValue || '';
    }

    console.log('Full phone number:', fullPhoneNumber);

    // Extraire l'indicatif et le numéro en utilisant libphonenumber-js
    let indicative = '';
    let phoneNumber = '';

    try {
      // Nettoyer le numéro (garder seulement les chiffres et le +)
      let cleanNumber = fullPhoneNumber.replace(/[^\d+]/g, '');
      
      // Si le numéro ne commence pas par +, essayer de déterminer s'il contient un indicatif
      // ou s'il s'agit d'un numéro local
      let numberWithPlus = cleanNumber;
      
      if (!cleanNumber.startsWith('+')) {
        // Vérifier si le numéro commence par un indicatif connu
        const countryCodes = {
          '221': 'SN', // Sénégal
          '225': 'CI', // Côte d'Ivoire
          '237': 'CM', // Cameroun
        };

        let foundCode = false;
        for (const [code, country] of Object.entries(countryCodes)) {
          // Vérifier si le numéro commence par l'indicatif (au moins 3 chiffres après)
          if (cleanNumber.startsWith(code) && cleanNumber.length > code.length + 2) {
            numberWithPlus = `+${cleanNumber}`;
            foundCode = true;
            break;
          }
        }

        // Si aucun indicatif trouvé au début, utiliser le pays par défaut (SN - Sénégal)
        if (!foundCode) {
          numberWithPlus = `+221${cleanNumber}`;
          console.log('Using default country code (SN) for:', cleanNumber);
        }
      }

      console.log('Processing phone number:', numberWithPlus);

      // Essayer de parser le numéro avec libphonenumber-js
      // D'abord, essayer avec les pays préférés
      const preferredCountries: CountryCode[] = ['SN', 'CI', 'CM'];
      let parsedNumber = null;

      for (const country of preferredCountries) {
        try {
          parsedNumber = parsePhoneNumber(numberWithPlus, country);
          if (parsedNumber && parsedNumber.isValid()) {
            console.log(`Successfully parsed with country ${country}:`, parsedNumber);
            break;
          }
        } catch (e) {
          // Continuer avec le pays suivant
        }
      }

      // Si le parsing avec les pays préférés a échoué, essayer sans pays
      if (!parsedNumber || !parsedNumber.isValid()) {
        try {
          parsedNumber = parsePhoneNumber(numberWithPlus);
          if (parsedNumber && parsedNumber.isValid()) {
            console.log('Successfully parsed without country:', parsedNumber);
          }
        } catch (e) {
          console.warn('Failed to parse phone number:', e);
        }
      }

      if (parsedNumber && parsedNumber.isValid()) {
        // Utiliser les données parsées
        indicative = parsedNumber.countryCallingCode;
        phoneNumber = parsedNumber.nationalNumber;
        console.log('Final parsed phone number:', { indicative, phoneNumber, full: numberWithPlus });
      } else {
        // Fallback: utiliser une approche manuelle mais améliorée
        // Chercher l'indicatif au début du numéro (après le +)
        const match = numberWithPlus.match(/^\+(\d{1,3})(\d+)$/);
        if (match) {
          indicative = match[1];
          phoneNumber = match[2];
          console.log('Manual parsing result:', { indicative, phoneNumber });
        } else {
          // Dernier recours: utiliser les indicatifs connus au début
          const countryCodes = {
            '221': 'SN', // Sénégal
            '225': 'CI', // Côte d'Ivoire
            '237': 'CM', // Cameroun
          };

          for (const [code, country] of Object.entries(countryCodes)) {
            if (numberWithPlus.startsWith(`+${code}`)) {
              indicative = code;
              phoneNumber = numberWithPlus.substring(code.length + 1);
              console.log('Country code found at start:', { indicative, phoneNumber });
              break;
            }
          }

          // Si toujours rien trouvé, utiliser le pays par défaut (Sénégal)
          if (!indicative) {
            indicative = '221';
            phoneNumber = cleanNumber.replace(/^\+?221/, ''); // Enlever 221 si présent
            if (!phoneNumber) {
              phoneNumber = cleanNumber; // Sinon utiliser le numéro tel quel
            }
            console.log('Using default country code (SN):', { indicative, phoneNumber });
          }
        }
      }
    } catch (error) {
      console.error('Error parsing phone number:', error);
      this.loading = false;
      this.ngxUiLoaderService.stopLoader('verify-phone-loader');
      this.swalService.error(
        'Erreur',
        'Erreur lors du traitement du numéro de téléphone.'
      );
      return;
    }

    const requestData: RequestOtpDto = {
      phoneNumber: phoneNumber,
      indicative: indicative,
    };

    console.log('Request OTP successful, opening modal with data:', {
      phoneNumber: requestData.phoneNumber,
      indicative: requestData.indicative,
    });

    // // Ouvrir le modal de validatio

    // // Écouter les événements du modal OTP

    this.authService.requestOtp(requestData).subscribe({
      next: (response) => {
        this.loading = false;
        this.ngxUiLoaderService.stopLoader('verify-phone-loader');

        console.log('Request OTP successful, opening modal with data:', {
          phoneNumber: requestData.phoneNumber,
          indicative: requestData.indicative,
        });
        const modalId = this.modalService.open({
          component: VerifyOtpComponent,
          data: {
            phoneNumber: requestData.phoneNumber,
            indicative: requestData.indicative,
          },
          title: 'Vérification OTP',
          width: '500px',
        });
        this.handleOtpModalEvents(modalId);

        // Émettre l'événement avec les données pour le composant parent
        this.otpRequested.emit({
          otpId: response.otpId,
          phoneNumber: requestData.phoneNumber,
          indicative: requestData.indicative,
        });
      },
      error: (error) => {
        this.loading = false;
        this.ngxUiLoaderService.stopLoader('verify-phone-loader');

        console.log(error);
        let errorMessage =
          error.error?.message ||
          "Une erreur est survenue lors de l'envoi du code OTP.";

        if (error.status === 409) {
          errorMessage =
            'Un utilisateur avec ce numéro de téléphone existe déjà';
        }

        // Afficher l'erreur avec Swal
        this.swalService.error('Erreur', errorMessage);
      },
    });
  }

  onPhoneInputChange(event: any) {
    console.log('onPhoneInputChange - Event:', event);
    console.log('onPhoneInputChange - Type:', typeof event);
    console.log('onPhoneInputChange - Value:', event);

    // Si c'est un objet, afficher ses propriétés
    if (typeof event === 'object' && event !== null) {
      console.log('onPhoneInputChange - Object keys:', Object.keys(event));
      for (const [key, value] of Object.entries(event)) {
        console.log(`onPhoneInputChange - ${key}:`, value);
      }
    }
  }

  private handleOtpModalEvents(modalId: string) {
    // Ici vous pouvez ajouter la logique pour gérer les événements du modal OTP
    // Par exemple, fermer le modal quand l'OTP est vérifié avec succès
    // ou rediriger vers la page suivante

    // Écouter les événements du modal OTP
    // Note: Cette logique sera gérée par le service modal
    // console.log('Handling OTP modal events for modal ID:', modalId);
  }

  onOtpVerified(otpData: any) {
    console.log('OTP verified successfully:', otpData);

    // Émettre l'événement avec l'otpId vers le composant parent
    this.otpRequested.emit({
      otpId: otpData.otpId,
      phoneNumber: otpData.phoneNumber,
      indicative: otpData.indicative,
    });
  }

  onOtpFailed(error: string) {
    console.log('OTP verification failed:', error);
    // Gérer l'échec de validation OTP si nécessaire
  }
}
